import React from 'react';
import Helmet from 'react-helmet';
import Waypoint from 'react-waypoint';
import { Route } from 'react-router-dom';
import debounce from 'lodash/debounce';

import fullname from 'utils/fullname';
import Users from 'api/resources/users';
import getErrors from 'utils/getErrors';
import { Deserializer } from 'jsonapi-serializer';

import UserPage from 'pages/UserPage';

import User from 'admin-moderator/containers/User';
import UserCardStub from 'admin-moderator/components/UserCardStub';

import IconUsers from 'icons/Users';

import FiltersDropdown from 'components/FiltersDropdown';
import SearchForm from 'components/SearchForm';
import Checkbox from 'components/Checkbox';

import RightColumn from 'styled/Page/RightColumn';
import Main from 'styled/Page/Main';
import CenteredContainer from 'styled/Page/CenteredContainer';
import ErrorText from 'styled/Page/ErrorText';
import FilterWrapper from 'styled/Page/FilterWrapper';
import GridList from 'styled/Page/GridList';
import PrimaryButton from 'styled/Buttons/PrimaryButton';

const PAGE_SIZE = 20;
const SORT_LIST = [
  {
    label: 'По алфавиту A-Я',
    value: 'name',
  }, {
    label: 'По алфавиту Я-А',
    value: '-name',
  },
];

export class UsersPage extends React.PureComponent {
  state = {
    users: [],
    errors: [],
    hasMore: false,
    loading: false,
    isBanned: false,
    pageNumber: 0,
    sort: SORT_LIST[0],
    searchQuery: undefined,
  };

  loadUsersPostsWithDebounce = debounce(() => {
    this.loadUsers();
  }, 500);

  componentDidMount() {
    this.loadUsers();
  }

  handleBanFilterChange = (e) => {
    this.setState({ isBanned: e.target.checked }, this.reloadUsers);
  }

  reloadUsers = async () => {
    this.setState({
      pageNumber: 0,
      users: [],
      hasMore: false,
    }, this.loadUsers);
  }

  handleBan = (user) => {
    this.setState(prevState => ({
      users: prevState.users.map(item => (item.id === user.id ? { ...item, currentBan: user.currentBan } : item)),
    }));
  }

  handleSearchChange = ({ target }) => {
    this.setState({
      searchQuery: target.value,
      pageNumber: 0,
      users: [],
      hasMore: false,
    }, this.loadUsersPostsWithDebounce);
  }

  handleSearchClose = () => {
    this.setState({
      searchQuery: undefined,
      pageNumber: 0,
      users: [],
      hasMore: false,
    }, this.loadUsers);
  }

  handleSortFilterChange = (sort = {}) => {
    this.setState({ sort }, this.reloadUsers);
  }

  handleSearchSubmit = (e) => {
    e.preventDefault();
    this.loadUsers();
  }

  loadUsers = async () => {
    if (this.state.loading) return;
    this.setState({ loading: true });

    try {
      const response = await Users.getAll({
        page: {
          number: this.state.pageNumber,
          size: PAGE_SIZE,
        },
        filter: {
          is_banned: this.state.isBanned ? undefined : false,
          q: this.state.searchQuery,
        },
        fields: {
          user: 'first_name,last_name,email,current_ban,material_counters,avatar',
        },
        sort: this.state.sort.value,
        include: 'avatar,current_ban',
      });

      const users = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      this.setState(prevState => ({
        users: prevState.users.concat(users),
        loading: false,
        hasMore: users.length === PAGE_SIZE,
        pageNumber: prevState.pageNumber + 1,
      }));
    } catch (err) {
      getErrors(err).then((errors) => {
        this.setState({ errors });
      });
      this.setState({ loading: false });
    }
  }

  render() {
    return (
      <Main>
        <Helmet><title>Пользователи</title></Helmet>

        <div style={{ width: '100%' }}>
          <FilterWrapper style={{ margin: '0 32px 0 25px' }}>
            <Checkbox
              id="isBanned"
              checked={this.state.isBanned}
              onChange={this.handleBanFilterChange}
              toggle
              disabled={this.state.loading}
            >
              Заблокированные
            </Checkbox>

            <FiltersDropdown
              filters={SORT_LIST}
              onFilterChange={this.handleSortFilterChange}
              style={{ marginRight: '24px', width: '170px' }}
              activeFilter={this.state.sort}
            />

            <div style={{ marginLeft: 'auto' }}>
              <SearchForm
                value={this.state.searchQuery}
                onChange={this.handleSearchChange}
                onSubmit={this.handleSearchSubmit}
                onClose={this.handleSearchClose}
              />
            </div>
          </FilterWrapper>

          <div style={{ padding: '10px 32px 100px' }}>
            <GridList>
              {this.state.users.map(user => (
                <User
                  id={user.id}
                  key={user.id}
                  name={fullname(user)}
                  materialCount={user.materialCounters
                    ? (user.materialCounters.images + user.materialCounters.videos)
                    : 0
                  }
                  email={user.email}
                  avatar={user.avatar && user.avatar.url}
                  isBanned={Boolean(user.currentBan)}
                />
              ))}

              {this.state.loading && (
                Array.from({ length: PAGE_SIZE }, () => <UserCardStub key={Math.random()} />)
              )}
            </GridList>

            {this.state.hasMore && <Waypoint onEnter={this.loadUsers} />}

            {this.state.hasMore && !this.state.loading && (
              <CenteredContainer>
                <PrimaryButton type="button" onClick={this.loadUsers}>Загрузить ещё</PrimaryButton>
              </CenteredContainer>
            )}

            {this.state.errors.length > 0 && (
              <CenteredContainer>
                {this.state.errors.map(error => (
                  <ErrorText role="alert" key={error}>{error}</ErrorText>
                ))}
              </CenteredContainer>
            )}

            {this.state.users.length === 0 && !this.state.loading && this.state.errors.length === 0 && (
              <CenteredContainer>
                <IconUsers size={80} />

                <ErrorText>Пользователей не найдено</ErrorText>
              </CenteredContainer>
            )}
          </div>
        </div>

        <RightColumn>
          <Route
            path="/users/:userId"
            exact
            render={() => (<UserPage onBan={this.handleBan} />)}
          />
        </RightColumn>
      </Main>
    );
  }
}

export default UsersPage;
